<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/interest_area_ledgers/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>

  <div class="ml-12">
    <interest-area-statistics-table
      :statistics="{
          total: filteredStatistics?.total,
          filtered: filteredStatistics?.filtered,
      }"
      :loading="loadingStatistics"
      :type="props.modelValue?.type"
    />
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :row-link="
          (item) => ({
            name: 'driver-points',
            params: { driverId: item.tour?.driver?.id ?? '-' },
          })
        "
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { reactive, computed, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider, withExtraOptions } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  HydraCollection,
  InterestAreaLedger,
  InterestZone,
  Payout
} from '@/entities/Entities';
import { useQueryRef } from '@hooks/url';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import {useTableFields} from '@hooks/table-configuration';
import InterestAreaStatisticsTable from '@utils/InterestAreaStatisticsTable.vue';
import { format } from 'date-fns';

interface Props {
  modelValue: InterestZone | null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.interestZoneDetails', {
    interestZone: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'interestArea.id': props.modelValue?.id,
}));
const totalFilter = computed(() => {
  const filters: any = { ...filter.value, ...fixedFilter.value };
  if (filters['pointLedgerEntry.amount'] === true) {
    filters['pointLedgerEntry.amount'] = { from: 1 };
  }
  if (filters['pointLedgerEntry.amount'] === false) {
    filters['pointLedgerEntry.amount'] = { to: 0 };
  }
  return filters;
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns = computed<ColumnDefinition[]>(() => ([
  {
    key: 'id',
    title: t('id'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    computed: (item: InterestAreaLedger) => item.pointLedgerEntry?.id ?? '-',
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'pointLedgerEntry.timestamp',
    title: t('interestAreaLedger.timestamp'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-',
    visible: true,
    sortable: true,
  },
  {
    key: 'tour.driver.user.firstName',
    title: t('user.firstName'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    computed: (item: InterestAreaLedger) => item.tour?.driver?.user?.firstName ?? '-',
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'tour.driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    computed: (item: InterestAreaLedger) => item.tour?.driver?.user?.lastName,
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'amount',
    title: props.modelValue?.type === 'distance' ? t('tour.distance') : t('tour.time'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    computed: (item: InterestAreaLedger) => {
      if (item.amount === null) {
        return '-';
      }
      if (props.modelValue?.type === 'distance') {
        return `${(item.amount / 1000).toFixed(2)} km`;
      }
      if (props.modelValue?.type === 'time') {
        return formatTimeDurationFromSeconds(item.amount);
      }
      return '-';
    },
    sortable: true,
    visible: true,
  },
  {
    key: 'pointLedgerEntry.amount',
    title: t('pointLedger.numberOfPoints'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    computed: (item: InterestAreaLedger) => item.pointLedgerEntry?.amount ?? '-',
    sortable: true,
    visible: true,
    filter: {
      type: 'bool',
      key: 'exists[pointLedgerEntry]',
      group: t('pages.interestZoneList.fieldGroups.general'),
      options: {
        label: t('pointLedger.receivedPoints'),
      },
    },
  }
]));

const visibleColumns = useTableFields(
  columns,
);

const blockRequest = computed(() => props.modelValue === null);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Payout>>('/interest_area_ledger_entries', providerParams, {
  disabled: blockRequest,
});

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<any>('/interest_areas/statistic', providerParams, {
  disabled: blockRequest,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  unref(columns).flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
